import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Plus, Trash2, AlertTriangle, Loader2, FileWarning } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const SEVERITY = {
  leve: { label: "Leve", color: "#6B7280" },
  grave: { label: "Grave", color: "#D97706" },
  muy_grave: { label: "Muy grave", color: "#E63946" },
};

export default function DriverSanctionsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [sanctions, setSanctions] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    reason: "",
    severity: "leve",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, s, c] = await Promise.all([
        api.get(`/drivers/${id}`),
        api.get(`/drivers/${id}/sanctions`),
        api.get(`/drivers/${id}/complaints`),
      ]);
      setDriver(d.data);
      setSanctions(s.data);
      setComplaints(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/drivers/${id}/sanctions`, form);
      toast.success("Amonestación registrada");
      setOpen(false);
      setForm({ date: new Date().toISOString().slice(0, 10), reason: "", severity: "leve", notes: "" });
      await load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const removeSanction = async (sid) => {
    if (!window.confirm("¿Eliminar esta amonestación?")) return;
    try {
      await api.delete(`/drivers/${id}/sanctions/${sid}`);
      toast.success("Amonestación eliminada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  if (!driver) return <div className="p-12 text-center text-zinc-500">Cargando…</div>;

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-6xl mx-auto" data-testid="driver-sanctions-page">
      <button
        onClick={() => navigate(`/drivers/${id}`)}
        className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black flex items-center gap-2 mb-6"
      >
        <ArrowLeft size={14} /> Volver al conductor
      </button>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 pb-6 border-b border-black">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            Informes de amonestación
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            {driver.name}
          </h1>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="rounded-none btn-primary h-11 px-5 text-xs uppercase tracking-widest"
          data-testid="new-sanction-btn"
        >
          <Plus size={14} className="mr-2" /> Nueva amonestación
        </Button>
      </header>

      <section className="border border-black bg-white mb-8">
        <div className="border-b border-black p-4 font-display font-semibold text-sm uppercase tracking-widest flex items-center gap-2">
          <AlertTriangle size={16} /> Amonestaciones manuales ({sanctions.length})
        </div>
        <div className="overflow-x-auto">
          <table className="ds-table w-full" data-testid="sanctions-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Motivo</th>
                <th>Gravedad</th>
                <th>Notas</th>
                <th>Registrada por</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-6 text-center text-zinc-500">Cargando…</td></tr>
              ) : sanctions.length === 0 ? (
                <tr><td colSpan={6} className="py-6 text-center text-zinc-500">Sin amonestaciones registradas.</td></tr>
              ) : sanctions.map((s) => {
                const sev = SEVERITY[s.severity] || SEVERITY.leve;
                return (
                  <tr key={s.id} data-testid={`sanction-row-${s.id}`}>
                    <td className="whitespace-nowrap">{s.date}</td>
                    <td className="font-semibold">{s.reason}</td>
                    <td>
                      <span
                        className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border"
                        style={{ borderColor: sev.color, color: sev.color }}
                      >
                        {sev.label}
                      </span>
                    </td>
                    <td className="text-sm text-zinc-600 max-w-xs">{s.notes || "—"}</td>
                    <td className="text-sm text-zinc-600">{s.created_by_name}</td>
                    <td className="text-right">
                      <button
                        onClick={() => removeSanction(s.id)}
                        className="text-[#E63946] hover:underline text-xs uppercase tracking-widest inline-flex items-center gap-1"
                        data-testid={`delete-sanction-${s.id}`}
                      >
                        <Trash2 size={12} /> Eliminar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="border border-black bg-white">
        <div className="border-b border-black p-4 font-display font-semibold text-sm uppercase tracking-widest flex items-center gap-2">
          <FileWarning size={16} /> Reclamaciones asociadas ({complaints.length})
        </div>
        <div className="overflow-x-auto">
          <table className="ds-table w-full" data-testid="driver-complaints-table">
            <thead>
              <tr>
                <th>Nº</th>
                <th>Fecha</th>
                <th>Vencimiento</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {complaints.length === 0 ? (
                <tr><td colSpan={5} className="py-6 text-center text-zinc-500">Sin reclamaciones asociadas.</td></tr>
              ) : complaints.map((c) => (
                <tr key={c.id}>
                  <td className="font-mono font-semibold">
                    <Link to={`/complaints/${c.id}`} className="hover:underline">{c.number}</Link>
                  </td>
                  <td>{c.complaint_date}</td>
                  <td>{c.deadline}</td>
                  <td className="text-xs uppercase tracking-widest">{c.status}</td>
                  <td>
                    {c.overdue && (
                      <span className="text-[10px] uppercase tracking-widest text-[#E63946] font-bold">
                        Vencida
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-black max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-tight">
              Nueva amonestación
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Fecha</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                  className="mt-2 h-11 rounded-none border-black"
                  data-testid="sanction-date"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Gravedad</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-none border-black" data-testid="sanction-severity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="leve">Leve</SelectItem>
                    <SelectItem value="grave">Grave</SelectItem>
                    <SelectItem value="muy_grave">Muy grave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Motivo *</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                required
                className="mt-2 h-11 rounded-none border-black"
                placeholder="Llegada tarde, comportamiento, etc."
                data-testid="sanction-reason"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Notas / Detalles</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-2 rounded-none border-black min-h-[100px]"
                data-testid="sanction-notes"
              />
            </div>
            <DialogFooter className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="rounded-none border-black h-11 px-5 text-xs uppercase tracking-widest"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-none btn-primary h-11 px-6 text-xs uppercase tracking-widest"
                data-testid="save-sanction-btn"
              >
                {saving ? <Loader2 className="animate-spin" size={14} /> : "Registrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
